import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Divider, Paper, Skeleton } from "@mui/material";
import {
  AccountBalanceOutlined,
  AssignmentOutlined,
  BookmarkBorder,
  Groups,
  ShieldOutlined,
} from "@mui/icons-material";
import { DocumentList, ExpProcessRow, ExpSectionRow, StepActionsExpediente } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setExpedienteEmpresaDoc, setExpedientePersonas } from "@store/slices/expedienteSlice";
import { validateRequiredDocumentsAreUploaded } from "@components/TabBofu/functions";
import {
  applyPersonDocumentChange,
  computePersonsIsComplete,
  PersonDocumentChange,
} from "@components/PersonForm/personHelpers";
import { isFormStatusEditable } from "@helpers/funciones";
import { BOFU_TABS_CONFIG, PATHS_EXPEDIENTE_V2 } from "@helpers/constantes";
import { BofuTabDocument } from "@interfaces/response";

export const TabEmpresaPFAE = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const empresa = useSelector((state: RootState) => state.expediente.empresa);
  const expedientePersonas = useSelector((state: RootState) => state.expediente.personas);
  const personaPFAE = useSelector((state: RootState) => state.expediente.personaPFAE);
  const isLoading = useSelector((state: RootState) => !state.expediente.tabsReady);

  const documentos = empresa.documents;
  const persons = expedientePersonas.persons;

  const [documentsByPerson, setDocumentsByPerson] = useState<Record<string, BofuTabDocument[]>>(
    expedientePersonas.documentsByPerson,
  );

  useEffect(() => {
    if (!isLoading) {
      setDocumentsByPerson(expedientePersonas.documentsByPerson);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const makeHandleDocumentChange = (personId: string) => (change: PersonDocumentChange) => {
    setDocumentsByPerson((current) => {
      const updated = {
        ...current,
        [personId]: applyPersonDocumentChange(current[personId] || [], change),
      };
      const updatedPersons = computePersonsIsComplete(persons, updated);
      dispatch(setExpedientePersonas({ ...expedientePersonas, persons: updatedPersons }));
      return updated;
    });
  };

  const {
    solicitud_credito: showSolicitudCredito,
    referencias_personales: showReferencias,
    informacion_bancaria: showInformacionBancaria,
  } = empresa.forms;

  const showAnyEmpresaForm = showSolicitudCredito.enabled || showReferencias || showInformacionBancaria;

  const showPersonalForm = personaPFAE?.showPersonalForm.enabled;
  const showIdentityValidation = personaPFAE?.showIdentityValidation.enabled;
  const showAnyPersonaForm = showPersonalForm || showIdentityValidation;

  const empresaFormsOk =
    (!showSolicitudCredito.enabled || !isFormStatusEditable(application.loan_application_information_status)) &&
    (!showReferencias || !isFormStatusEditable(application.references_status)) &&
    (!showInformacionBancaria || !isFormStatusEditable(application.bank_information_status));

  const personaFormsOk =
    (!showPersonalForm || !isFormStatusEditable(personaPFAE?.personal_information_status ?? "PENDIENTE")) &&
    (!showIdentityValidation || personaPFAE?.identity_validation_completed === true);

  const personaDocsOk = validateRequiredDocumentsAreUploaded(
    personaPFAE?.id ? (documentsByPerson[personaPFAE.id] ?? []) : [],
  );

  const nextDisabled =
    isLoading ||
    !empresaFormsOk ||
    !personaFormsOk ||
    !validateRequiredDocumentsAreUploaded(documentos) ||
    !personaDocsOk;

  const PATH_SOLICITUD = PATHS_EXPEDIENTE_V2.informacionSolicitud.path;
  const PATH_REFERENCIAS = PATHS_EXPEDIENTE_V2.referencias.path;
  const PATH_INFO_BANCARIA = PATHS_EXPEDIENTE_V2.informacionBancaria.path;

  return (
    <Box>
      {isLoading ? (
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
      ) : (
        <Fragment>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {showAnyEmpresaForm && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <Paper variant="lendia_paper" sx={{ p: "18px 20px" }}>
                  {showSolicitudCredito.enabled && (
                    <>
                      <ExpProcessRow
                        heading="Información de la solicitud"
                        hint="Proporciona tu contacto, transaccionales y declaraciones regulatorias."
                        status={application.loan_application_information_status}
                        icon={<BookmarkBorder sx={{ fontSize: 18 }} />}
                        label="LLenar"
                        onClick={() => navigate(PATH_SOLICITUD)}
                        disabled={!isFormStatusEditable(application.loan_application_information_status)}
                        data-testid="expediente-empresa-solicitud-button"
                      />
                      {(showReferencias || showInformacionBancaria) && <Divider sx={{ my: "18px" }} />}
                    </>
                  )}

                  {showReferencias && (
                    <>
                      <ExpProcessRow
                        heading="Referencias comerciales"
                        hint="Clientes y proveedores que podamos contactar para validar tu operación."
                        status={application.references_status}
                        icon={<Groups sx={{ fontSize: 18 }} />}
                        label="Llenar"
                        onClick={() => navigate(PATH_REFERENCIAS)}
                        disabled={!isFormStatusEditable(application.references_status)}
                        data-testid="expediente-empresa-referencias-button"
                      />
                      {showInformacionBancaria && <Divider sx={{ my: "18px" }} />}
                    </>
                  )}

                  {showInformacionBancaria && (
                    <ExpProcessRow
                      heading="Información bancaria"
                      hint="Cuenta de la empresa donde depositaremos el crédito y desde donde se domiciliarán los pagos."
                      status={application.bank_information_status}
                      icon={<AccountBalanceOutlined sx={{ fontSize: 18 }} />}
                      label="Llenar"
                      onClick={() => navigate(PATH_INFO_BANCARIA)}
                      disabled={!isFormStatusEditable(application.bank_information_status)}
                      data-testid="expediente-empresa-bancaria-button"
                    />
                  )}
                </Paper>
              </Box>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {showAnyPersonaForm && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  {showPersonalForm && (
                    <>
                      <ExpProcessRow
                        heading="Información personal"
                        hint="Datos de contacto, domicilio y situación laboral"
                        status={personaPFAE.personal_information_status ?? "PENDIENTE"}
                        icon={<AssignmentOutlined sx={{ fontSize: 18 }} />}
                        label="Llenar"
                        onClick={() => navigate(`${PATHS_EXPEDIENTE_V2.formularioPersona.path}/${personaPFAE.id}`)}
                        disabled={!isFormStatusEditable(personaPFAE.personal_information_status ?? "PENDIENTE")}
                        data-testid="expediente-pfae-form-button"
                      />
                      {personaPFAE.showIdentityValidation.enabled && <Divider sx={{ my: "16px" }} />}
                    </>
                  )}

                  {showIdentityValidation && (
                    <ExpProcessRow
                      heading="Verifica tu identidad"
                      hint="Selfie con prueba de vida + identificación oficial"
                      status={personaPFAE.identity_validation_completed === true ? "COMPLETO" : "PENDIENTE"}
                      icon={<ShieldOutlined sx={{ fontSize: 18 }} />}
                      label="Validar"
                      onClick={() => navigate(`${PATHS_EXPEDIENTE_V2.validacionIdentidad.path}/${personaPFAE.id}`)}
                      disabled={
                        !isFormStatusEditable(
                          personaPFAE.identity_validation_completed === true ? "COMPLETO" : "PENDIENTE",
                        )
                      }
                      data-testid="expediente-pfae-identidad-button"
                    />
                  )}
                </Paper>
              )}

              <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                <ExpSectionRow heading="Documentos">
                  {(personaPFAE?.id ? (documentsByPerson[personaPFAE.id] ?? []) : []).length > 0 && (
                    <Fragment>
                      <DocumentList
                        key={personaPFAE?.id}
                        documents={documentsByPerson[personaPFAE?.id ?? ""] ?? []}
                        onChange={makeHandleDocumentChange(personaPFAE?.id ?? "")}
                        onChangeMultiple={makeHandleDocumentChange(personaPFAE?.id ?? "")}
                      />

                      <Divider sx={{ my: "16px" }} />
                    </Fragment>
                  )}

                  <DocumentList
                    documents={documentos}
                    onChange={({ document, newValue }) =>
                      dispatch(
                        setExpedienteEmpresaDoc({
                          document,
                          newValue,
                          valueAction: undefined,
                          valueIndex: undefined,
                        }),
                      )
                    }
                    onChangeMultiple={({ document, newValue, valueAction, valueIndex }) =>
                      dispatch(setExpedienteEmpresaDoc({ document, newValue, valueAction, valueIndex }))
                    }
                  />
                </ExpSectionRow>
              </Paper>
            </Box>
          </Box>

          <StepActionsExpediente
            currentTab={BOFU_TABS_CONFIG.tab_empresa.code}
            nextDisabled={nextDisabled}
            showRevision={!expedientePersonas.is_active}
          />
        </Fragment>
      )}
    </Box>
  );
};
